// src/pages/surveys/SurveyPreviewPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { surveyService } from '../../services/survey.service';
import { Survey } from '../../types/survey.types';
import { SurveyPreview } from '../../components/surveys/preview';

const SurveyPreviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSurvey = async () => {
      if (!id) {
        setError('No survey ID provided');
        setIsLoading(false);
        return;
      }

      try {
        const surveyData = await surveyService.get(id);
        if (surveyData) {
          setSurvey(surveyData);
        } else {
          setError('Survey not found');
        }
      } catch (err) {
        console.error('Error loading survey:', err);
        setError('Failed to load survey');
      } finally {
        setIsLoading(false);
      }
    };

    loadSurvey();
  }, [id]);

  const handleClose = () => {
    navigate('/surveys');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error || !survey) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-medium text-gray-900">{error || 'Survey not found'}</h2>
          <button
            onClick={handleClose}
            className="mt-4 text-indigo-600 hover:text-indigo-700"
          >
            Back to Surveys
          </button>
        </div>
      </div>
    );
  }

  return <SurveyPreview survey={survey} onClose={handleClose} mode="preview" />;
};

export default SurveyPreviewPage;
